import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, Pencil, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { API } from './stars';
import { ScheduleCalendar } from './ScheduleCalendar';

const HAZ = [['az_tehlikeli', 'Az Tehlikeli'], ['tehlikeli', 'Tehlikeli'], ['cok_tehlikeli', 'Çok Tehlikeli']];
const DAYS = [[0, 'Pzt'], [1, 'Sal'], [2, 'Çar'], [3, 'Per'], [4, 'Cum'], [5, 'Cmt'], [6, 'Paz']];
const EMPTY = { hazard_classes: [], location_ids: [], is_tipi: [], is_cesidi: [], weekdays: [], start_time: '', end_time: '' };

const Chips = ({ label, options, value, onChange, testId }) => (
  <div>
    <p className="mb-1 text-sm font-medium text-slate-700">{label} <span className="text-xs font-normal text-slate-400">{value.length ? `${value.length} seçili` : 'Hepsi'}</span></p>
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map(([v, l]) => {
        const on = value.includes(v);
        return <button key={v} type="button" onClick={() => onChange(on ? value.filter(x => x !== v) : [...value, v])} data-testid={`${testId}-${v}`}
          className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${on ? 'border-amber-500 bg-amber-500 font-semibold text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>{l}</button>;
      })}
    </div>
  </div>
);

const summary = (r, locs) => [
  r.weekdays.length ? DAYS.filter(([d]) => r.weekdays.includes(d)).map(([, l]) => l).join(', ') : 'Her gün',
  r.start_time ? `${r.start_time}–${r.end_time}` : 'Tüm gün',
  r.hazard_classes.length ? HAZ.filter(([v]) => r.hazard_classes.includes(v)).map(([, l]) => l).join(', ') : 'Tüm tehlike sınıfları',
  r.location_ids.length ? r.location_ids.map(id => locs[id] || '-').join(', ') : 'Tüm lokasyonlar',
  r.is_tipi.length ? r.is_tipi.join(', ') : 'Tüm iş tipleri',
  r.is_cesidi.length ? r.is_cesidi.join(', ') : 'Tüm iş çeşitleri',
];

const RuleDialog = ({ rule, expert, lists, onClose, onSaved }) => {
  const [f, setF] = useState({ ...EMPTY, ...rule });
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const kinds = lists.kinds.filter(k => !f.is_tipi.length || f.is_tipi.includes(lists.typeName[k.job_type_id]));
  const save = async () => {
    try {
      const body = { ...f, user_id: expert.id };
      if (rule.id) await axios.put(`${API}/five-star/schedules/${rule.id}`, body); else await axios.post(`${API}/five-star/schedules`, body);
      toast.success('Program kuralı kaydedildi'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Kaydedilemedi'); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="schedule-dialog">
        <DialogHeader><DialogTitle>{expert.name} · {rule.id ? 'Kuralı Düzenle' : 'Yeni Kural'}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Boş bırakılan alan "hepsi" demektir. Talep bu kurala uyarsa denetim sorumluluğu bu uzmana atanır.</p>
        <div className="space-y-4">
          <Chips label="Günler" options={DAYS} value={f.weekdays} onChange={set('weekdays')} testId="schedule-days" />
          <div>
            <p className="mb-1 text-sm font-medium text-slate-700">Çalışma saat aralığı <span className="text-xs font-normal text-slate-400">boşsa tüm gün</span></p>
            <div className="flex items-center gap-2">
              <Input type="time" value={f.start_time} onChange={e => set('start_time')(e.target.value)} className="w-32" data-testid="schedule-start-time" />–
              <Input type="time" value={f.end_time} onChange={e => set('end_time')(e.target.value)} className="w-32" data-testid="schedule-end-time" />
            </div>
          </div>
          <Chips label="Tehlike sınıfı" options={HAZ} value={f.hazard_classes} onChange={set('hazard_classes')} testId="schedule-hazard" />
          <Chips label="Lokasyon" options={lists.locations.map(l => [l.id, l.name])} value={f.location_ids} onChange={set('location_ids')} testId="schedule-locations" />
          <Chips label="İş tipi" options={lists.types.map(t => [t.name, t.name])} value={f.is_tipi} onChange={set('is_tipi')} testId="schedule-types" />
          <Chips label="İş çeşidi" options={[...new Set(kinds.map(k => k.name))].map(n => [n, n])} value={f.is_cesidi} onChange={set('is_cesidi')} testId="schedule-kinds" />
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Vazgeç</Button><Button onClick={save} data-testid="schedule-save">Kaydet</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Süper Admin: İSG uzmanlarının çalışma programı (denetim sorumluluğu ataması)
export const ScheduleCard = () => {
  const [experts, setExperts] = useState([]);
  const [rules, setRules] = useState([]);
  const [lists, setLists] = useState({ locations: [], types: [], kinds: [], typeName: {} });
  const [edit, setEdit] = useState(null);
  const load = () => axios.get(`${API}/five-star/schedules`).then(r => setRules(r.data)).catch(() => {});
  useEffect(() => {
    load();
    axios.get(`${API}/five-star/auditors`).then(r => setExperts(r.data)).catch(() => {});
    Promise.all([axios.get(`${API}/locations`), axios.get(`${API}/job-types`), axios.get(`${API}/job-kinds`)]).then(([l, t, k]) =>
      setLists({ locations: l.data, types: t.data, kinds: k.data, typeName: Object.fromEntries(t.data.map(x => [x.id, x.name])) })).catch(() => {});
  }, []);
  const locs = Object.fromEntries(lists.locations.map(l => [l.id, l.name]));
  const del = async (id) => { if (!window.confirm('Kural silinsin mi?')) return; await axios.delete(`${API}/five-star/schedules/${id}`); load(); };
  return (
    <Card className="max-w-5xl border-slate-200" data-testid="schedule-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-slate-600" />İSG Uzmanı Çalışma Programı</CardTitle>
        <CardDescription>Her uzmana gün, saat aralığı, tehlike sınıfı, lokasyon, iş tipi ve iş çeşidine göre kurallar tanımlayın. Talebe uyan kuralların uzmanları denetimden sorumlu olur; geç yapılan ve kaçırılan denetimler onların performansına yazılır. Hiçbir kurala uymayan talep "Program dışı" sayılır ve lokasyondan sorumlu tüm İSG uzmanlarına yazılır.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ScheduleCalendar rules={rules} experts={experts} locations={lists.locations} />
        {experts.map(e => {
          const mine = rules.filter(r => r.user_id === e.id);
          return (
            <div key={e.id} className="rounded-lg border p-3" data-testid={`schedule-expert-${e.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-800">{e.name} <span className="text-xs font-normal text-slate-500">· {mine.length} kural</span></p>
                <Button size="sm" variant="outline" onClick={() => setEdit({ expert: e, rule: {} })} data-testid={`schedule-add-${e.id}`}><Plus className="mr-1 h-4 w-4" />Kural Ekle</Button>
              </div>
              {mine.length === 0 && <p className="mt-1 text-xs text-slate-400">Program yok; yalnızca lokasyon yetkisine göre program dışı taleplerden sorumlu olur.</p>}
              {mine.map(r => (
                <div key={r.id} className="mt-2 flex items-start justify-between gap-2 rounded bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid={`schedule-rule-${r.id}`}>
                  <span className="flex flex-wrap gap-1">{summary(r, locs).map((x, i) => <span key={i} className="rounded bg-white px-1.5 py-0.5 ring-1 ring-slate-200">{x}</span>)}</span>
                  <span className="flex shrink-0 gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit({ expert: e, rule: r })} data-testid={`schedule-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => del(r.id)} data-testid={`schedule-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </span>
                </div>
              ))}
            </div>
          );
        })}
        {experts.length === 0 && <p className="text-sm text-slate-400">İSG uzmanı kullanıcısı yok</p>}
      </CardContent>
      {edit && <RuleDialog {...edit} lists={lists} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </Card>
  );
};
